/* eslint-disable @nx/enforce-module-boundaries -- dto/core barrel imports predate in-out helper extraction */
import {
  ForemanEmployeeDispatch,
  ForemanEmployeeInOut,
  ForemanEmployeeLabor,
  ForemanEquipmentUsage,
  ForemanJobDetail,
  HoursWorked,
} from '@ups/xplat/api/dto';
import { Guid } from '@ups/xplat/core';
import moment from 'moment';
import type { ForemanJobService } from './foreman-job.service';
import {
  cloneInOutsForNewLabor,
  havePersistableInOutsChanged,
  isPersistableForemanEmployeeInOut,
} from './foreman-employee-in-out.utils';
import { markForemanLaborDraftChanged } from './foreman-labor-draft.utils';

export function bulkUpdateLabors(
  service: ForemanJobService,
  workOrderId: string,
  shift: number,
  hoursWorked: HoursWorked
) {
  const currentJob = service.selectedJob();
  const employees = service.getEmployeesForTimeEntry();

  for (const employee of employees) {
    if (!employee || !employee.foremanEmployeeJobID) {
      continue;
    }

    const foremanPhaseID = workOrderId || employee.foremanPhaseID;
    if (!foremanPhaseID) continue;

    updateEmployeeLabors(
      service,
      currentJob,
      employee,
      foremanPhaseID,
      shift,
      hoursWorked,
      null,
      [],
      true
    );
  }

  service.selectedJob.set({ ...currentJob });
}

export function updateEmployeeLabors(
  service: ForemanJobService,
  currentJob: ForemanJobDetail,
  employee: ForemanEmployeeDispatch,
  foremanPhaseID: string,
  shift: number,
  hoursWorked: HoursWorked,
  notes: string,
  inOuts: ForemanEmployeeInOut[],
  isBulk?: boolean,
  isException?: boolean,
  exceptionNotes?: string | null
) {
  if (!employee.labors) {
    employee.labors = [];
  }

  const labors = (employee.labors || []).filter(
    (l) =>
      moment(l.dateWorked).format('MM/DD/YYYY') ===
        moment(service.selectedDate()).format('MM/DD/YYYY') &&
      l.shift === shift &&
      l.foremanJobCrewID === service.selectedForemanCrewID() &&
      l.foremanPhaseID === foremanPhaseID
  );

  const deactivateLabor = (labor: ForemanEmployeeLabor) => {
    const wasActive = labor.bActive === true;
    const hadHours = Number(labor.hours || 0) !== 0;

    if (wasActive || hadHours) {
      markForemanLaborDraftChanged(labor);
    }

    labor.bActive = false;
    labor.hours = 0;
  };

  for (const earnCode of Object.keys(hoursWorked)) {
    const requestedHours = Number(hoursWorked[earnCode] || 0);
    const matchingLabors = labors.filter(
      (l) => (l.foremanEarnCode || '').toLowerCase() === earnCode
    );

    if (!requestedHours) {
      matchingLabors.forEach(deactivateLabor);
      continue;
    }

    let labor =
      matchingLabors.find((l) => l.bActive === true) || matchingLabors[0];

    if (!labor) {
      labor = {
        foremanEmployeeLaborID: Guid.newGuid(),
        foremanEmployeeJobID: employee.foremanEmployeeJobID,
        dateWorked: service.selectedDate(),
        foremanPhaseID,
        shift,
        foremanJobCrewID: service.selectedForemanCrewID(),
        foremanEarnCode: earnCode.toUpperCase(),
        isChanged: true,
      } as ForemanEmployeeLabor;
      markForemanLaborDraftChanged(labor);

      employee.labors.push(labor);
    } else if (
      labor.hours !== requestedHours ||
      labor.bActive !== true ||
      labor.foremanNotes !== notes ||
      labor.isException !== isException ||
      labor.foremanExceptionNotes !== exceptionNotes
    ) {
      markForemanLaborDraftChanged(labor);
    }

    labor.hours = requestedHours;
    labor.bActive = true;

    if (!isBulk) {
      labor.foremanNotes = notes;
      labor.isException = isException ?? false;
      labor.foremanExceptionNotes = exceptionNotes ?? null;
    }

    if (earnCode === 'hw' || earnCode === 'st') {
      const nextInOuts = (inOuts || [])
        .filter(isPersistableForemanEmployeeInOut)
        .map((i) => ({
          ...i,
          foremanEmployeeLaborID: labor.foremanEmployeeLaborID,
        }));
      if (havePersistableInOutsChanged(labor.inOuts, nextInOuts)) {
        markForemanLaborDraftChanged(labor);
      }
      labor.inOuts = nextInOuts;
    }

    matchingLabors
      .filter((l) => l.foremanEmployeeLaborID !== labor.foremanEmployeeLaborID)
      .forEach(deactivateLabor);
  }

  if (!isBulk) {
    const normalizedExceptionNotes = exceptionNotes ?? null;
    const activeLabors = labors.filter((labor) => labor.bActive === true);

    for (const labor of activeLabors) {
      if (
        labor.isException !== (isException ?? false) ||
        labor.foremanExceptionNotes !== normalizedExceptionNotes
      ) {
        markForemanLaborDraftChanged(labor);
      }

      labor.isException = isException ?? false;
      labor.foremanExceptionNotes = normalizedExceptionNotes;
    }
  }

  employee.foremanPhaseID = employee.foremanPhaseID || foremanPhaseID;
  service.setEmployeeWorkOrder(employee, foremanPhaseID);
  service.calculateTotalHours(employee, service.selectedDate());

  if (!isBulk) {
    service.updateEmployee(employee);
  }
}

export function setAbsentReasonOther(
  service: ForemanJobService,
  employee: ForemanEmployeeDispatch,
  absentReasonOther: string
) {
  const otherReason = service.absentReasons.find((a) => a.name === 'Other');
  setAbsent(service, employee, otherReason.id, absentReasonOther);
}

export function setAbsent(
  service: ForemanJobService,
  employee: ForemanEmployeeDispatch,
  absentReasonID: string,
  absentReasonOther: string
) {
  if (!employee.labors) {
    employee.labors = [];
  }

  const labors = employee.labors.filter(
    (l) =>
      moment(l.dateWorked).format('MM/DD/YYYY') ===
      moment(service.selectedDate()).format('MM/DD/YYYY')
  );

  labors.forEach((l) => {
    if (l.bActive) {
      l.bActive = false;
      markForemanLaborDraftChanged(l);
    }
  });

  let absentRecord = labors.find((l) => l.isAbsent === true);

  if (!absentRecord) {
    absentRecord = {
      foremanEmployeeLaborID: Guid.newGuid(),
      foremanEmployeeJobID: employee.foremanEmployeeJobID,
      dateWorked: service.selectedDate(),
      foremanEarnCode: 'ST',
      isChanged: true,
    } as ForemanEmployeeLabor;
    markForemanLaborDraftChanged(absentRecord);

    employee.labors.push(absentRecord);
  } else if (
    absentRecord.absentReasonID !== absentReasonID ||
    absentRecord.absentReasonOther !== absentReasonOther ||
    absentRecord.bActive !== true
  ) {
    markForemanLaborDraftChanged(absentRecord);
  }

  const absentReason =
    service.absentReasons.find((a) => a.id === absentReasonID) ||
    service.absentReasons[0];

  absentRecord.bActive = true;
  absentRecord.isAbsent = true;
  absentRecord.absentReasonID = absentReasonID;
  absentRecord.absentReason = absentReason.name;
  absentRecord.absentReasonOther = absentReasonOther;

  service.calculateTotalHours(employee, service.selectedDate());
  const currentJob = service.selectedJob();
  service.selectedJob.set({ ...currentJob });
}

export function clearAbsent(
  service: ForemanJobService,
  employee: ForemanEmployeeDispatch
) {
  if (!employee.labors) {
    employee.labors = [];
  }

  const labors = employee.labors.filter(
    (l) =>
      moment(l.dateWorked).format('MM/DD/YYYY') ===
      moment(service.selectedDate()).format('MM/DD/YYYY')
  );

  const absentRecord = labors.find((l) => l.isAbsent === true);

  if (absentRecord && absentRecord.bActive) {
    absentRecord.bActive = false;
    markForemanLaborDraftChanged(absentRecord);
  }

  service.calculateTotalHours(employee, service.selectedDate());
  const currentJob = service.selectedJob();
  service.selectedJob.set({ ...currentJob });
}

export function copyPreviousDay(service: ForemanJobService) {
  const previousDay = moment(service.selectedDate())
    .subtract(1, 'day')
    .toDate();
  const currentDay = service.selectedDate();

  if (!service.selectedJob()) {
    return;
  }

  const currentJob = service.selectedJob();

  for (const employee of currentJob.employees) {
    const previousDayLabors = (employee.labors || []).filter(
      (l) =>
        moment(l.dateWorked).format('MM/DD/YYYY') ===
          moment(previousDay).format('MM/DD/YYYY') &&
        l.bActive &&
        !l.isAbsent
    );

    const currentDayLabors = (employee.labors || []).filter(
      (l) =>
        moment(l.dateWorked).format('MM/DD/YYYY') ===
        moment(currentDay).format('MM/DD/YYYY')
    );
    // Must dirty + clear: #30217 remote overlay would otherwise resurrect
    // server-committed target-day rows on the next job-list refresh.
    currentDayLabors.forEach((l) => {
      const wasActive = l.bActive === true;
      const hadHours = Number(l.hours || 0) !== 0;
      if (wasActive || hadHours) {
        markForemanLaborDraftChanged(l);
      }
      l.bActive = false;
      l.hours = 0;
    });

    for (const prevLabor of previousDayLabors) {
      const newLaborId = Guid.newGuid();
      const newLabor: ForemanEmployeeLabor = {
        foremanEmployeeLaborID: newLaborId,
        foremanEmployeeJobID: employee.foremanEmployeeJobID,
        dateWorked: currentDay,
        foremanEarnCode: prevLabor.foremanEarnCode,
        shift: prevLabor.shift,
        foremanJobCrewID: prevLabor.foremanJobCrewID,
        hours: prevLabor.hours,
        foremanPhaseID: prevLabor.foremanPhaseID,
        bActive: true,
        isAbsent: false,
        absentReasonID: '',
        isChanged: true,
        absentReason: '',
        absentReasonOther: '',
        foremanNotes: prevLabor.foremanNotes || '',
        inOuts: cloneInOutsForNewLabor(
          prevLabor.inOuts,
          newLaborId,
          employee.foremanEmployeeJobID
        ),
      };
      markForemanLaborDraftChanged(newLabor);

      employee.labors = [...(employee.labors || []), newLabor];
    }

    service.calculateTotalHours(employee, currentDay);
  }

  for (const equipment of currentJob.equipment) {
    const previousDayUsages = (equipment.usages || []).filter(
      (u) =>
        moment(u.dateUsed).format('MM/DD/YYYY') ===
          moment(previousDay).format('MM/DD/YYYY') && u.bActive
    );

    const currentDayUsages = (equipment.usages || []).filter(
      (u) =>
        moment(u.dateUsed).format('MM/DD/YYYY') ===
        moment(currentDay).format('MM/DD/YYYY')
    );
    currentDayUsages.forEach((u) => {
      const wasActive = u.bActive === true;
      const hadAmount = Number(u.amount || 0) !== 0;
      if (wasActive || hadAmount) {
        u.isChanged = true;
      }
      u.bActive = false;
    });

    for (const prevUsage of previousDayUsages) {
      const newUsage: ForemanEquipmentUsage = {
        foremanEquipmentUsageID: Guid.newGuid(),
        foremanEquipmentDispatchID: equipment.foremanEquipmentDispatchID,
        dateUsed: currentDay,
        foremanPhaseID: prevUsage.foremanPhaseID,
        shift: prevUsage.shift,
        foremanJobCrewID: prevUsage.foremanJobCrewID,
        quantity: prevUsage.quantity,
        amount: prevUsage.amount,
        bActive: true,
        notUsed: prevUsage.notUsed,
        foremanNotes: null,
        isChanged: true,
      };

      equipment.usages = [...(equipment.usages || []), newUsage];
    }

    service.calculateTotalHoursUsed(equipment);
  }

  service.selectedJob.set({ ...currentJob });
}
